/**
 * Detects the actual data type even when value is stored as string
 */
export const detectActualDataType = (value, sampleValues = []) => {
  if (value === null || value === undefined || value === "") {
    return "string";
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return "object";
  }

  if (Array.isArray(value)) {
    return "array";
  }

  const strValue = String(value).trim();

  // Check boolean
  if (
    strValue === "true" ||
    strValue === "false" ||
    typeof value === "boolean"
  ) {
    return "boolean";
  }

  // Check date
  if (isDateString(strValue)) {
    return "date";
  }

  // Check number
  if (isNumericString(strValue)) {
    if (isCurrencyString(strValue)) {
      return "currency";
    }
    if (isPercentageString(strValue)) {
      return "percentage";
    }
    return "number";
  }

  // Check email
  if (isEmailString(strValue)) {
    return "email";
  }

  // Check phone
  if (isPhoneString(strValue)) {
    return "phone";
  }

  // Check URL
  if (isURLString(strValue)) {
    return "url";
  }

  return "string";
};

const isDateString = (str) => {
  if (!str) return false;

  // ISO 8601 format
  const isoPattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
  if (isoPattern.test(str)) {
    const date = new Date(str);
    return !isNaN(date.getTime());
  }

  // Common date formats
  const datePatterns = [
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,
    /^\d{1,2}-\d{1,2}-\d{4}$/,
    /^\d{4}\/\d{1,2}\/\d{1,2}$/,
    /^\d{1,2}\s+\w+\s+\d{4}$/,
    /^\w+\s+\d{1,2},?\s+\d{4}$/,
  ];

  if (datePatterns.some((pattern) => pattern.test(str))) {
    const date = new Date(str);
    return !isNaN(date.getTime());
  }

  return false;
};

const isNumericString = (str) => {
  if (!str) return false;
  const cleaned = str.replace(/[$,\s%]/g, "");
  return !isNaN(cleaned) && !isNaN(parseFloat(cleaned));
};

const isCurrencyString = (str) => {
  if (!str) return false;
  const currencyPattern = /^[$€£¥₹]?\s*\d+([,.]\d{2,3})*([,.]\d{2})?$/;
  return currencyPattern.test(str);
};

const isPercentageString = (str) => {
  if (!str) return false;
  const percentPattern = /^\d+(\.\d+)?%$/;
  return percentPattern.test(str);
};

const isEmailString = (str) => {
  if (!str) return false;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(str);
};

const isPhoneString = (str) => {
  if (!str) return false;
  const digits = str.replace(/\D/g, "");
  return (
    digits.length >= 7 && digits.length <= 15 && /[\d\s\-\(\)\+]/.test(str)
  );
};

const isURLString = (str) => {
  if (!str) return false;
  try {
    new URL(str);
    return true;
  } catch {
    const urlPattern =
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    return urlPattern.test(str);
  }
};

/**
 * Enhanced column type detection
 */
export const getEnhancedColumnDataType = (column, rows) => {
  // If dataType is explicitly defined, use it (PRIORITY)
  if (column.dataType) {
    return column.dataType;
  }

  // Get sample values
  const sampleSize = Math.min(rows.length, 100);
  const sampleValues = rows
    .slice(0, sampleSize)
    .map((row) => {
      if (column.valueGetter) {
        return column.valueGetter(row);
      }
      return row[column.key];
    })
    .filter((val) => val !== null && val !== undefined && val !== "");

  if (sampleValues.length === 0) {
    return "string";
  }

  const firstValue = sampleValues[0];
  return detectActualDataType(firstValue, sampleValues);
};

/**
 * Convert string value to appropriate type for comparison
 */
export const convertToType = (value, dataType) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  switch (dataType) {
    case "number":
    case "integer":
    case "currency":
    case "percentage":
      const cleaned = String(value).replace(/[$,\s%€£¥₹]/g, "");
      return parseFloat(cleaned);

    case "date":
      return new Date(value);

    case "boolean":
      if (typeof value === "boolean") return value;
      return String(value).toLowerCase() === "true";

    case "array":
      if (Array.isArray(value)) return value;
      try {
        return JSON.parse(value);
      } catch {
        return String(value)
          .split(",")
          .map((v) => v.trim());
      }

    case "object":
      if (typeof value === "object") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }

    default:
      return String(value);
  }
};

/**
 * Compare two values based on their type
 */
export const compareValues = (value1, value2, operator, dataType) => {
  const v1 = convertToType(value1, dataType);
  const v2 = convertToType(value2, dataType);

  switch (operator) {
    case "equals":
    case "equal":
      if (dataType === "date") {
        return new Date(v1).getTime() === new Date(v2).getTime();
      }
      return v1 === v2;

    case "notEqual":
      if (dataType === "date") {
        return new Date(v1).getTime() !== new Date(v2).getTime();
      }
      return v1 !== v2;

    case "greaterThan":
      if (dataType === "date") {
        return new Date(v1) > new Date(v2);
      }
      return v1 > v2;

    case "lessThan":
      if (dataType === "date") {
        return new Date(v1) < new Date(v2);
      }
      return v1 < v2;

    case "greaterThanOrEqual":
      if (dataType === "date") {
        return new Date(v1) >= new Date(v2);
      }
      return v1 >= v2;

    case "lessThanOrEqual":
      if (dataType === "date") {
        return new Date(v1) <= new Date(v2);
      }
      return v1 <= v2;

    case "contains":
      return String(v1).toLowerCase().includes(String(v2).toLowerCase());

    case "notContains":
      return !String(v1).toLowerCase().includes(String(v2).toLowerCase());

    case "startsWith":
      return String(v1).toLowerCase().startsWith(String(v2).toLowerCase());

    case "endsWith":
      return String(v1).toLowerCase().endsWith(String(v2).toLowerCase());

    case "isEmpty":
      return !v1 || v1 === "";

    case "isNotEmpty":
      return v1 && v1 !== "";

    case "between":
      if (Array.isArray(v2) && v2.length === 2) {
        if (dataType === "date") {
          const date = new Date(v1);
          return date >= new Date(v2[0]) && date <= new Date(v2[1]);
        }
        return v1 >= v2[0] && v1 <= v2[1];
      }
      return true;

    default:
      return true;
  }
};

/**
 * Enhanced version to replace determineColumnDataType
 */
export const determineColumnDataTypeEnhanced = (
  selectedColumn,
  rows,
  condition,
  index
) => {
  if (!selectedColumn || !condition) return;

  const detectedType = getEnhancedColumnDataType(selectedColumn, rows);

  condition.dataType = detectedType;

  switch (detectedType) {
    case "array":
      condition.filterValue = [];
      break;
    case "boolean":
      condition.filterValue = false;
      break;
    case "date":
      condition.filterValue = new Date().toISOString().split("T")[0];
      break;
    default:
      condition.filterValue = "";
  }

  return condition;
};
